import { Parser, type AssetId } from "@repo/caip";
import { z } from "zod";
import {
  EvmAccountIdSchema,
  ExecutionCorrelationIdSchema,
  ExecutionPortfolioIdSchema,
  ExecutionRequestKeySchema,
  ExpectedMarketOrderVenueSchema,
  NonNegativeIntegerAmountSchema,
  PositiveIntegerAmountSchema,
  SpecDrivenBuyFundPlanWireSchema,
  SpecDrivenBuyFundingExplanationWireSchema,
  SpecDrivenBuyPlanBasisSchema,
} from "./requests.js";
import {
  ExecutionMarketOrderQuotePreviewSchema,
  ExecutionOperationResponseSchema,
  ExecutionSpecDrivenBuyCreatedFundingSchema,
  LiveDescriptorSchema,
} from "./responses.js";

/**
 * One asset-trade front door. The caller states intent only (which asset,
 * which side, how much); the engine decides the cash asset, venue, funding
 * pre-steps, minimums, dust tolerance, and market hours.
 */
export const AssetTradeSideSchema = z.enum(["buy", "sell"]);

/**
 * An EVM tradable asset on a safe positive chain: an ERC-20 token or the
 * chain's native coin (`slip44:60`). Canonicalized to lowercase so retries
 * that differ only in address casing share one intent fingerprint.
 */
export const AssetTradeAssetIdSchema = z
  .string()
  .trim()
  .superRefine((assetId, ctx) => {
    try {
      const parsed = Parser.parseAssetId(assetId as AssetId);
      const chainId = Number(parsed.chain.reference);
      const erc20 =
        parsed.asset.namespace === "erc20" &&
        /^0x[a-fA-F0-9]{40}$/u.test(parsed.asset.reference);
      const native =
        parsed.asset.namespace === "slip44" && parsed.asset.reference === "60";
      if (
        parsed.chain.namespace !== "eip155" ||
        !/^[1-9]\d*$/u.test(parsed.chain.reference) ||
        !Number.isSafeInteger(chainId) ||
        (!erc20 && !native)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "assetId must identify an EVM ERC-20 token or native coin",
        });
      }
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "assetId must be a parseable CAIP-19 id",
      });
    }
  })
  .transform((assetId) => assetId.toLowerCase());

/** Buys are exact-in: `cashRaw` is the most cash the trade may debit. */
export const AssetTradeBuyAmountSchema = z
  .object({ cashRaw: PositiveIntegerAmountSchema })
  .strict();

/** Sells name an exact asset amount, or everything spendable after holds. */
export const AssetTradeSellAmountSchema = z.union([
  z.object({ assetRaw: PositiveIntegerAmountSchema }).strict(),
  z.object({ all: z.literal(true) }).strict(),
]);

/**
 * Engine-signed preview material (HMAC domain `atb1`). It binds the reviewed
 * portfolio, asset, side, amount, destination account, venue, cash asset,
 * funding plan, gross cap, and expiry. Create refuses a basis that fails
 * verification, has expired, or names a venue the engine no longer routes to.
 */
export const AssetTradePlanBasisSchema = z
  .object({
    version: z.literal(1),
    expiresAt: z.number().int().nonnegative(),
    chainId: z.number().int().positive(),
    destinationAccountRef: EvmAccountIdSchema,
    cashAssetId: z.string().min(1),
    venue: ExpectedMarketOrderVenueSchema,
    /** Buys: the most cash the whole trade (funding included) may debit. */
    quotedGrossCapRaw: PositiveIntegerAmountSchema.nullable(),
    /**
     * Buys that must first move cash from other accounts: the exact trade
     * spend plus the engine's signed funding plan. Null when local cash covers
     * the trade.
     */
    funding: z
      .object({
        spendRaw: PositiveIntegerAmountSchema,
        basis: SpecDrivenBuyPlanBasisSchema,
      })
      .strict()
      .nullable(),
    integrity: z.string().regex(/^atb1\.[a-f0-9]{64}$/u),
  })
  .strict();

const assetTradeIdentityShape = {
  /**
   * Webapp recovery relies on `market:<portfolioId>:<side>:<assetId>:<uuid>`
   * keys; the engine persists the key verbatim.
   */
  requestKey: ExecutionRequestKeySchema,
  correlationId: ExecutionCorrelationIdSchema.optional(),
  portfolioId: ExecutionPortfolioIdSchema,
  assetId: AssetTradeAssetIdSchema,
} satisfies z.ZodRawShape;

const refineDestinationChain = (
  input: {
    readonly assetId: string;
    readonly destinationAccountRef?: string;
  },
  ctx: z.RefinementCtx,
): void => {
  if (input.destinationAccountRef === undefined) return;
  const destination = Parser.parseAccountId(input.destinationAccountRef);
  const asset = Parser.parseAssetId(input.assetId as AssetId);
  if (destination.chain.reference !== asset.chain.reference) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "destination account must be on the asset chain",
      path: ["destinationAccountRef"],
    });
  }
};

export const AssetTradePreviewRequestSchema = z
  .discriminatedUnion("side", [
    z
      .object({
        ...assetTradeIdentityShape,
        side: z.literal("buy"),
        amount: AssetTradeBuyAmountSchema,
        destinationAccountRef: EvmAccountIdSchema.optional(),
      })
      .strict(),
    z
      .object({
        ...assetTradeIdentityShape,
        side: z.literal("sell"),
        amount: AssetTradeSellAmountSchema,
        destinationAccountRef: EvmAccountIdSchema.optional(),
      })
      .strict(),
  ])
  .superRefine(refineDestinationChain);

/**
 * `planBasis` is required for owner-session and capability callers. Only
 * internal-key callers (durable automation) may omit it; the engine then
 * compiles fresh and never adds funding pre-steps on their behalf.
 */
export const AssetTradeCreateRequestSchema = z.discriminatedUnion("side", [
  z
    .object({
      ...assetTradeIdentityShape,
      side: z.literal("buy"),
      amount: AssetTradeBuyAmountSchema,
      planBasis: AssetTradePlanBasisSchema.optional(),
    })
    .strict(),
  z
    .object({
      ...assetTradeIdentityShape,
      side: z.literal("sell"),
      amount: AssetTradeSellAmountSchema,
      planBasis: AssetTradePlanBasisSchema.optional(),
    })
    .strict(),
]);

export const AssetTradeDisabledReasonSchema = z.enum([
  /** Hold-adjusted balance does not cover the trade (and no funding applies). */
  "insufficient_balance",
  /** Other accounts cannot cover the buy's cash shortfall. */
  "insufficient_funding_sources",
  /** Nothing spendable to sell. */
  "nothing_to_sell",
  /** The order is below the venue's minimum notional. */
  "below_minimum",
  "market_closed",
  "market_paused",
  /** The venue returned a quote it cannot execute. */
  "quote_not_executable",
]);

export const AssetTradeMarketWindowSchema = z
  .object({
    /**
     * `open` for venues that trade continuously; `unknown` when the issuer
     * status could not be read (create re-checks and fails closed).
     */
    state: z.enum(["open", "closed", "paused", "unknown"]),
    nextOpenAt: z.string().nullable(),
    affectedAssets: z.array(z.string()),
  })
  .strict();

export const AssetTradeFundingPreviewSchema = z
  .object({
    status: z.enum(["planned", "insufficient"]),
    buyingPowerRaw: NonNegativeIntegerAmountSchema,
    /** The funding plan, or the best partial plan when insufficient. */
    plan: SpecDrivenBuyFundPlanWireSchema.nullable(),
    explanation: SpecDrivenBuyFundingExplanationWireSchema,
    /** Rail cost between the gross debit and what reaches the trade. */
    feeRaw: NonNegativeIntegerAmountSchema,
    /** Gross cash the plan leaves where it is. */
    retainedRaw: NonNegativeIntegerAmountSchema,
    /** Cash the owner would need to deposit when the plan is partial. */
    depositShortfallRaw: NonNegativeIntegerAmountSchema,
  })
  .strict();

export const AssetTradePreviewResponseSchema = z
  .object({
    flow: z.literal("asset_trade"),
    portfolioId: z.string().min(1),
    assetId: z.string().min(1),
    side: AssetTradeSideSchema,
    chainId: z.number().int().positive(),
    cashAsset: z
      .object({
        assetId: z.string().min(1),
        symbol: z.string().min(1),
        decimals: z.number().int().nonnegative(),
      })
      .strict(),
    /** Buys: gross cash debited and the cash that reaches the trade. */
    spend: z
      .object({
        grossRaw: PositiveIntegerAmountSchema,
        deliverableRaw: PositiveIntegerAmountSchema,
      })
      .strict()
      .nullable(),
    quote: ExecutionMarketOrderQuotePreviewSchema.nullable(),
    funding: AssetTradeFundingPreviewSchema.nullable(),
    marketWindow: AssetTradeMarketWindowSchema,
    minimum: z
      .object({
        cashAssetId: z.string().min(1),
        minimumRaw: PositiveIntegerAmountSchema,
        requestedRaw: NonNegativeIntegerAmountSchema.nullable(),
      })
      .strict()
      .nullable(),
    /** Hold-adjusted balance of what the trade sells (cash for buys). */
    availability: z
      .object({
        requiredRaw: NonNegativeIntegerAmountSchema,
        availableRaw: NonNegativeIntegerAmountSchema,
        heldRaw: NonNegativeIntegerAmountSchema,
      })
      .strict(),
    disabledReasons: z.array(AssetTradeDisabledReasonSchema),
    canSubmit: z.boolean(),
    expiresAt: z.number().int().nonnegative(),
    /** Always present when `canSubmit`; null otherwise. */
    planBasis: AssetTradePlanBasisSchema.nullable(),
  })
  .strict()
  .superRefine((preview, ctx) => {
    if (preview.canSubmit !== (preview.planBasis !== null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "planBasis is present exactly when canSubmit",
        path: ["planBasis"],
      });
    }
    if (preview.canSubmit && preview.disabledReasons.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "a submittable preview carries no disabled reasons",
        path: ["disabledReasons"],
      });
    }
  });

export const AssetTradeOperationResponseSchema =
  ExecutionOperationResponseSchema.extend({
    flow: z.literal("asset_trade"),
    side: AssetTradeSideSchema,
    provider: z.string().min(1),
    funding: ExecutionSpecDrivenBuyCreatedFundingSchema.nullable(),
    live: LiveDescriptorSchema,
    portfolioLive: LiveDescriptorSchema,
  });

/**
 * `GET /execution/trades/:operationId/funding`: the funding plan a funded buy
 * persisted at admission, for clients that lost the create response. `null`
 * when the operation moved no cash first.
 */
export const AssetTradeFundingRecoveryResponseSchema = z
  .object({
    funding: ExecutionSpecDrivenBuyCreatedFundingSchema.nullable(),
  })
  .strict();

/**
 * `POST /execution/trades/max`: the most the engine would accept as this
 * trade's `amount` right now. Callers name scope only; the engine reads every
 * balance itself.
 */
export const AssetTradeMaxRequestSchema = z
  .object({
    portfolioId: ExecutionPortfolioIdSchema,
    side: AssetTradeSideSchema,
    assetId: AssetTradeAssetIdSchema,
  })
  .strict();

/** Why a buy Max is zero. */
export const AssetTradeBuyMaxReasonSchema = z.enum([
  /** No spendable cash in any of the portfolio's accounts. */
  "no_cash",
  /** Cash is moving between accounts (a bridge not yet proven landed). */
  "cash_in_transit",
  /** The portfolio has no execution account on the asset's chain. */
  "no_account",
]);

/** Why a sell Max is zero. */
export const AssetTradeSellMaxReasonSchema = z.enum([
  /** The execution account holds none of the asset. */
  "nothing_to_sell",
  /** Every unit held is reserved by an open hold. */
  "fully_reserved",
]);

const assetTradeMaxObservationShape = {
  portfolioId: z.string().min(1),
  assetId: z.string().min(1),
  capturedAt: z.iso.datetime(),
  /** Server-clock deadline; compare only with `capturedAt`. */
  expiresAt: z.iso.datetime(),
} satisfies z.ZodRawShape;

/**
 * `amount` uses the same keys as the trade request, in the same units: a buy
 * Max is `cashRaw` of `cashAsset` (the cash this trade settles in), a sell Max
 * is `assetRaw` of `assetId`. `reason` is set exactly when the amount is zero.
 * Admission gates that do not depend on balance (market hours, minimums,
 * quote executability) stay with the preview.
 */
export const AssetTradeMaxResponseSchema = z
  .discriminatedUnion("side", [
    z
      .object({
        ...assetTradeMaxObservationShape,
        side: z.literal("buy"),
        cashAsset: z
          .object({
            assetId: z.string().min(1),
            symbol: z.string().min(1),
            decimals: z.number().int().nonnegative(),
          })
          .strict(),
        amount: z.object({ cashRaw: NonNegativeIntegerAmountSchema }).strict(),
        reason: AssetTradeBuyMaxReasonSchema.nullable(),
      })
      .strict(),
    z
      .object({
        ...assetTradeMaxObservationShape,
        side: z.literal("sell"),
        amount: z.object({ assetRaw: NonNegativeIntegerAmountSchema }).strict(),
        reason: AssetTradeSellMaxReasonSchema.nullable(),
      })
      .strict(),
  ])
  .superRefine((max, ctx) => {
    const raw =
      "cashRaw" in max.amount ? max.amount.cashRaw : max.amount.assetRaw;
    if ((raw === "0") !== (max.reason !== null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "reason is present exactly when the maximum is zero",
        path: ["reason"],
      });
    }
    if (Date.parse(max.expiresAt) <= Date.parse(max.capturedAt)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "expiresAt must follow capturedAt",
        path: ["expiresAt"],
      });
    }
  });

export type AssetTradeSide = z.infer<typeof AssetTradeSideSchema>;
export type AssetTradeMaxRequest = z.infer<typeof AssetTradeMaxRequestSchema>;
export type AssetTradeMaxResponse = z.infer<typeof AssetTradeMaxResponseSchema>;
export type AssetTradeBuyMaxReason = z.infer<
  typeof AssetTradeBuyMaxReasonSchema
>;
export type AssetTradeSellMaxReason = z.infer<
  typeof AssetTradeSellMaxReasonSchema
>;
export type AssetTradePlanBasis = z.infer<typeof AssetTradePlanBasisSchema>;
export type AssetTradePreviewRequest = z.infer<
  typeof AssetTradePreviewRequestSchema
>;
export type AssetTradeCreateRequest = z.infer<
  typeof AssetTradeCreateRequestSchema
>;
export type AssetTradeDisabledReason = z.infer<
  typeof AssetTradeDisabledReasonSchema
>;
export type AssetTradeMarketWindow = z.infer<
  typeof AssetTradeMarketWindowSchema
>;
export type AssetTradeFundingPreview = z.infer<
  typeof AssetTradeFundingPreviewSchema
>;
export type AssetTradePreviewResponse = z.infer<
  typeof AssetTradePreviewResponseSchema
>;
export type AssetTradeOperationResponse = z.infer<
  typeof AssetTradeOperationResponseSchema
>;
export type AssetTradeFunding = z.infer<
  typeof ExecutionSpecDrivenBuyCreatedFundingSchema
>;
export type AssetTradeFundingRecoveryResponse = z.infer<
  typeof AssetTradeFundingRecoveryResponseSchema
>;
